import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Hover-out grace period (ms). When the pointer leaves a trigger it usually has
 * to cross a small dead zone before reaching the dropdown — most visibly the
 * detached floating card, which sits in a gap below the header. We wait this long
 * before actually closing so re-entering the menu cancels the close; the dropdown
 * only shuts if the pointer truly left.
 */
const HOVER_CLOSE_DELAY = 300;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // Click-trigger mode: close the open dropdown on a click outside the menu, or
    // on Escape. No-ops in hover mode (guarded inside the handlers).
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body?.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    document.documentElement.removeAttribute('scroll-lock');
    clearTimeout(this.#closeTimer);
    this.#cleanupMutationObserver();
  }

  /**
   * Whether dropdowns open on click (vs. hover). Set by the section schema via
   * the `data-dropdown-trigger` attribute on the <header-menu> element.
   * @returns {boolean}
   */
  get #clickTrigger() {
    return this.dataset.dropdownTrigger === 'click';
  }

  /**
   * Click-trigger handler (bound via `on:click="/toggle"` on each top-level item).
   * Opens the item's dropdown, or closes it if already open. Items without a
   * dropdown fall through to normal link navigation.
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    // Horizon's declarative `on:click` delegation invokes this with `event.target`
    // rewritten to the directive-bearing element (the top-level <li>), NOT the
    // element actually clicked. The real clicked node is the first entry of the
    // event's composed path — use that for hit-testing, or the guard below (and
    // findMenuItem) can never tell an in-dropdown click from a top-level one.
    const clicked = (typeof event.composedPath === 'function' && event.composedPath()[0]) || event.target;
    if (!(clicked instanceof Element)) return;

    // Clicks inside the open submenu are real navigation/interaction — leave them
    // alone so a click on the dropdown's content (or its padding/gaps) never
    // toggles the menu shut.
    if (clicked.closest('[ref="submenu[]"]')) return;

    const item = findMenuItem(clicked);
    if (!item) return;

    const hasDropdown = Boolean(findSubmenu(item)) || Boolean(clicked.closest('[slot="more"]'));
    // Plain top-level link (no dropdown) → let the browser navigate.
    if (!hasDropdown) return;

    // Has a dropdown → intercept the click and toggle it instead of navigating.
    event.preventDefault();

    if (this.#state.activeItem === item) {
      this.#deactivate(item);
    } else {
      this.activate(event);
    }
  };

  /**
   * Close the open dropdown when the pointer goes down outside the menu (click mode).
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#clickTrigger || !this.#state.activeItem) return;
    // Normal case: the pointerdown lands on an element inside the menu DOM.
    if (event.target instanceof Node && this.contains(event.target)) return;
    // Fall-through guard: the open dropdown uses a clip-path (full-width band
    // mode) whose bottom edge can sit just short of the rendered content, so a
    // pointerdown over the dropdown's lower padding/gaps gets clipped out of
    // hit-testing and resolves to the page *behind* it — failing contains()
    // above and wrongly closing the menu. Test the pointer geometrically against
    // the visible dropdown box instead; a click anywhere inside it stays open.
    if (this.#pointerInsideOpenDropdown(event)) return;
    this.#deactivate();
  };

  /**
   * Whether a pointer event falls within the open dropdown's visible box. Uses
   * the inner content element (`.menu-list__submenu-inner`) so it matches the
   * actual dropdown in both modes: the full-width band (inner spans the viewport)
   * and the floating card (inner is the card, so clicks in the page gutters
   * beside it still count as outside and close the menu).
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  #pointerInsideOpenDropdown(event) {
    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu) return false;
    const box = submenu.querySelector('.menu-list__submenu-inner') ?? submenu;
    const rect = box.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  }

  /**
   * Close the open dropdown on Escape and return focus to its trigger (click mode).
   * @param {KeyboardEvent} event
   */
  #onDocumentKeyDown = (event) => {
    if (!this.#clickTrigger || event.key !== 'Escape' || !this.#state.activeItem) return;
    const trigger = this.#state.activeItem.querySelector('[ref="menuitem"]') ?? this.#state.activeItem;
    this.#deactivate();
    if (trigger instanceof HTMLElement) trigger.focus();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Pending hover-out close timer (see HOVER_CLOSE_DELAY). Set when the pointer
   * leaves a trigger; cleared the instant it re-enters the menu.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // The pointer is back inside the menu (the same item, its dropdown, or a
    // sibling), so cancel any pending hover-out close before it fires.
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Only report expansion on items that actually control a dropdown. A plain
    // top-level link (no submenu, no overflow) must NOT get aria-expanded='true':
    // it's wrong for assistive tech, and in floating-card mode the pointer notch
    // is gated on [aria-expanded='true'], so an unconditional set draws a diamond
    // under link-only items on hover/focus.
    item.ariaExpanded = submenu ? 'true' : 'false';

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Lock page scroll while the dropdown is open so the page behind it can't
      // scroll out from under the open mega-menu — tall dropdowns scroll inside
      // their own capped box instead. Reuses the theme's shared scroll-lock
      // attribute (html[scroll-lock] { overflow: hidden } in base.css), the same
      // mechanism dialogs/drawers use. Released in #deactivate / disconnectedCallback.
      document.documentElement.setAttribute('scroll-lock', '');

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
      // No dropdown for this item — make sure scroll isn't left locked (e.g. when
      // moving from a dropdown item straight onto a plain top-level link).
      document.documentElement.removeAttribute('scroll-lock');
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    // Settle the open dropdown's reveal clip-path. The submenu's clip-path (CSS)
    // reads --full-open-header-height to clip the dropdown open from the header
    // down. When activation runs before the submenu's content is laid out its
    // height measures 0, so the clip resolves to the collapsed/closed geometry —
    // and a clip-path built from a CSS variable does not reliably re-resolve when
    // we later set the real height. A stuck collapsed clip removes the whole open
    // dropdown from hit-testing: clicks fall through to the page behind it, so the
    // menu's links are dead and (in click mode) a click inside closes the menu.
    // Re-assert the height against the laid-out submenu on the next frames, forcing
    // the clip-path to re-resolve to the open geometry.
    if (submenu) this.#settleOpenClip(submenu, finalHeight);
  };

  /**
   * Re-assert the open submenu height after layout settles so the reveal
   * clip-path re-resolves to the open geometry (otherwise it can stay stuck on
   * the closed-state value, clipping the dropdown out of hit-testing).
   * @param {HTMLElement} submenu
   * @param {number} fallbackHeight
   */
  #settleOpenClip(submenu, fallbackHeight) {
    const item = this.#state.activeItem;

    // Double rAF mirrors the height-measure timing used for deferred content, so
    // we read the laid-out height rather than the content-visibility intrinsic size.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Bail if the dropdown was closed or switched to another item meanwhile.
        if (this.#state.activeItem !== item) return;

        const headerComponent = this.headerComponent;
        if (!headerComponent) return;

        const height = Math.max(submenu.offsetHeight, fallbackHeight);
        if (height <= 0) return;

        // Re-assert against the laid-out height. On every real open this differs
        // from the closed-state value the clip cached (0), so the clip-path
        // re-resolves to the open geometry — keeping the reveal animation and the
        // "clip starts at header height" pointer protection intact.
        headerComponent.style.setProperty('--submenu-height', `${height}px`);
        this.#setFullOpenHeaderHeight(height);
      });
    });
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Pointer left the trigger. Don't close instantly — give a short grace period
    // so the pointer can cross the gap into the dropdown (especially the detached
    // floating card) without it shutting underneath. Re-entering the menu fires
    // `activate`, which clears this. Keyboard exits (blur) close immediately.
    if (event.type === 'pointerleave') {
      this.#scheduleClose();
      return;
    }

    this.#deactivate();
  }

  /**
   * Defer closing the open dropdown by HOVER_CLOSE_DELAY. A final geometric check
   * keeps it open if the pointer actually ended up over the dropdown (it may have
   * drifted in without re-firing `pointerenter` on a tracked element).
   */
  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#pointerInsideOpenDropdown({ clientX: this.#lastPointer.x, clientY: this.#lastPointer.y })) return;
      this.#deactivate();
    }, HOVER_CLOSE_DELAY);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body?.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Release the page scroll lock set when the dropdown opened.
    document.documentElement.removeAttribute('scroll-lock');

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // The event target may be the menuitem itself (or a child of it) — as happens
  // with click-to-open, where the click lands on the link/its inner span — or an
  // ancestor list item (hover, where pointerenter fires on the <li>). Resolve
  // both: prefer the menuitem at/above the target, else search descendants.
  const self = /** @type {HTMLElement | null} */ (element.closest('[ref="menuitem"]'));
  if (self) return self;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
